// Collapsible row (snippets/collapsible-row.liquid, blocks/collapsible-row*.liquid).
//
// <mv-collapsible-row> wraps a <details> header and its sibling content panel (the panel sits
// outside <details> so closing can animate — see component-collapsible.css). The element keeps
// the summary's aria-expanded, the root's `mv-crow--open` class (custom open-state colours) and
// the panel's inert state in step with the native open/closed toggle, and opens the row when the
// theme editor selects the block or one of its nested blocks (shopify:block:select).
(() => {
  const define = (window.Multiverse && Multiverse.defineOnce) || ((name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  });

  class CollapsibleRow extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      this.summary = this.details ? this.details.querySelector('summary') : null;
      this.panel = this.querySelector(':scope > .mv-crow__panel');
      if (!this.details) return;

      // The toggle listener lives on the child <details>, so it is bound once per node.
      if (!this._bound) {
        this._bound = true;
        this.details.addEventListener('toggle', () => this.sync());
      }
      this.sync();

      // Editor auto-open: the document listener is removed on disconnect, so it is (re)attached
      // on every connect (a block moved in the editor is disconnected and reconnected).
      if (window.Shopify && Shopify.designMode) {
        if (!this._onSelect) {
          this._onSelect = (event) => {
            if (event.target === this || this.contains(event.target)) this.open();
          };
        }
        document.removeEventListener('shopify:block:select', this._onSelect);
        document.addEventListener('shopify:block:select', this._onSelect);
      }
    }

    disconnectedCallback() {
      if (this._onSelect) document.removeEventListener('shopify:block:select', this._onSelect);
    }

    get isOpen() {
      return !!(this.details && this.details.open);
    }

    open() {
      if (this.details) this.details.open = true;
    }

    close() {
      if (this.details) this.details.open = false;
    }

    toggle() {
      if (this.details) this.details.open = !this.details.open;
    }

    sync() {
      const open = this.isOpen;
      this.classList.toggle('mv-crow--open', open);
      if (this.summary) this.summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.panel) {
        // Keep closed content out of the tab order / accessibility tree; it stays in the DOM so
        // nested blocks (forms, timers) keep their state.
        if (open) this.panel.removeAttribute('inert');
        else this.panel.setAttribute('inert', '');
      }
      this.dispatchEvent(new CustomEvent('mv:collapsible-toggle', { bubbles: true, detail: { open } }));
    }
  }
  define('mv-collapsible-row', CollapsibleRow);
})();
